/**
 * ETF Classifications
 *
 * Categorizes ETFs by asset class for portfolio allocation purposes.
 * Add new ETFs as needed when they appear in portfolios.
 */

/**
 * Asset class classification for portfolio allocation
 */
export enum AssetClass {
  UsEquity = 'us_equity',              // US stocks, US stock ETFs
  InternationalEquity = 'international_equity', // International stocks, VXUS, etc.
  EmergingMarkets = 'emerging_markets', // EM stocks/ETFs
  UsBonds = 'us_bonds',                // US bonds, BND, etc.
  InternationalBonds = 'international_bonds', // International bonds
  RealEstate = 'real_estate',          // REITs
  Commodities = 'commodities',         // Gold, commodities ETFs
  Crypto = 'crypto',                   // Cryptocurrency
  Cash = 'cash',                       // Cash, money market
  Other = 'other',                     // Uncategorized
}


// ============================================================================
// Granular Classifications (for Rebalancer)
// ============================================================================

/**
 * US Equity ETFs - US stock market index funds
 */
export const US_EQUITY_ETFS = [
  // Broad Market
  'VTI',   // Vanguard Total Stock Market ETF
  'VOO',   // Vanguard S&P 500 ETF
  'SPY',   // SPDR S&P 500 ETF
  'IVV',   // iShares Core S&P 500 ETF
  'SCHB',  // Schwab U.S. Broad Market ETF
  'ITOT',  // iShares Core S&P Total U.S. Stock Market ETF

  // Growth/Value
  'VUG',   // Vanguard Growth ETF
  'VTV',   // Vanguard Value ETF
  'IWF',   // iShares Russell 1000 Growth ETF
  'IWD',   // iShares Russell 1000 Value ETF

  // Small/Mid Cap
  'VB',    // Vanguard Small-Cap ETF
  'IWM',   // iShares Russell 2000 ETF (Small Cap)
  'VO',    // Vanguard Mid-Cap ETF
  'IJH',   // iShares Core S&P Mid-Cap ETF

  // Sector-Specific
  'VGT',   // Vanguard Information Technology ETF
  'VHT',   // Vanguard Health Care ETF
  'VFH',   // Vanguard Financials ETF
  'VDE',   // Vanguard Energy ETF
  'VNQ',   // Vanguard Real Estate ETF (note: also in real_estate)
  'QQQ',   // Invesco QQQ (Nasdaq-100)
];

/**
 * International Equity ETFs - Developed markets (ex-US)
 */
export const INTERNATIONAL_EQUITY_ETFS = [
  'VXUS',  // Vanguard Total International Stock ETF
  'VEA',   // Vanguard FTSE Developed Markets ETF
  'EFA',   // iShares MSCI EAFE ETF
  'IXUS',  // iShares Core MSCI Total International Stock ETF
  'SCHF',  // Schwab International Equity ETF
  'IEFA',  // iShares Core MSCI EAFE ETF
];

/**
 * Emerging Markets Equity ETFs
 */
export const EMERGING_MARKETS_ETFS = [
  'VWO',   // Vanguard FTSE Emerging Markets ETF
  'EEM',   // iShares MSCI Emerging Markets ETF
  'IEMG',  // iShares Core MSCI Emerging Markets ETF
  'SCHE',  // Schwab Emerging Markets Equity ETF
];

/**
 * US Bond ETFs - Fixed income securities including treasuries, corporates, munis, and high-yield
 */
export const US_BOND_ETFS = [
  // Municipal Bonds
  'VTEB',  // Vanguard Tax-Exempt Bond ETF
  'MUB',   // iShares National Muni Bond ETF
  'TFI',   // SPDR Nuveen Bloomberg Municipal Bond ETF

  // Total Bond Market
  'BND',   // Vanguard Total Bond Market ETF
  'AGG',   // iShares Core U.S. Aggregate Bond ETF
  'SCHZ',  // Schwab U.S. Aggregate Bond ETF

  // Treasury Bonds
  'TLT',   // iShares 20+ Year Treasury Bond ETF
  'IEF',   // iShares 7-10 Year Treasury Bond ETF
  'SHY',   // iShares 1-3 Year Treasury Bond ETF
  'VGIT',  // Vanguard Intermediate-Term Treasury ETF
  'VGSH',  // Vanguard Short-Term Treasury ETF
  'VGLT',  // Vanguard Long-Term Treasury ETF

  // Corporate Bonds
  'LQD',   // iShares iBoxx Investment Grade Corporate Bond ETF
  'VCIT',  // Vanguard Intermediate-Term Corporate Bond ETF
  'VCSH',  // Vanguard Short-Term Corporate Bond ETF

  // High-Yield Bonds
  'HYG',   // iShares iBoxx High Yield Corporate Bond ETF
  'JNK',   // SPDR Bloomberg High Yield Bond ETF

  // TIPS (Inflation-Protected)
  'TIP',   // iShares TIPS Bond ETF
  'VTIP',  // Vanguard Short-Term Inflation-Protected Securities ETF

  // Vanguard Bond Duration Series
  'BSV',   // Vanguard Short-Term Bond ETF
  'BIV',   // Vanguard Intermediate-Term Bond ETF
  'BLV',   // Vanguard Long-Term Bond ETF
];

/**
 * International Bond ETFs
 */
export const INTERNATIONAL_BOND_ETFS = [
  'BNDX',  // Vanguard Total International Bond ETF
  'IAGG',  // iShares Core International Aggregate Bond ETF
  'IGOV',  // iShares International Treasury Bond ETF
];

/**
 * Real Estate ETFs - REITs and real estate
 */
export const REAL_ESTATE_ETFS = [
  'VNQ',   // Vanguard Real Estate ETF
  'SCHH',  // Schwab U.S. REIT ETF
  'IYR',   // iShares U.S. Real Estate ETF
  'USRT',  // iShares Core U.S. REIT ETF
  'VNQI',  // Vanguard Global ex-U.S. Real Estate ETF
];

/**
 * Commodity ETFs - Precious metals, oil, etc.
 */
export const COMMODITIES_ETFS = [
  'GLD',   // SPDR Gold Shares
  'SLV',   // iShares Silver Trust
  'IAU',   // iShares Gold Trust
  'USO',   // United States Oil Fund
  'UNG',   // United States Natural Gas Fund
  'DBA',   // Invesco DB Agriculture Fund
  'DBC',   // Invesco DB Commodity Index Tracking Fund
  'GDX',   // VanEck Gold Miners ETF
  'GDXJ',  // VanEck Junior Gold Miners ETF
];

/**
 * Cryptocurrency ETFs - Bitcoin and crypto exposure
 */
export const CRYPTO_ETFS = [
  'IBIT',  // iShares Bitcoin Trust
  'GBTC',  // Grayscale Bitcoin Trust
  'ETHE',  // Grayscale Ethereum Trust
  'BITO',  // ProShares Bitcoin Strategy ETF
  'BTCO',  // Invesco Galaxy Bitcoin ETF
];


// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if a symbol is in a list of ETF symbols
 */
function isInList(symbol: string, list: string[]): boolean {
  const upperSymbol = symbol.toUpperCase().trim();
  return list.some(etf => upperSymbol.includes(etf));
}

// ============================================================================
// Granular Classification Functions (for Rebalancer)
// ============================================================================

export function isUSEquityETF(symbol: string): boolean {
  return isInList(symbol, US_EQUITY_ETFS);
}

export function isInternationalEquityETF(symbol: string): boolean {
  return isInList(symbol, INTERNATIONAL_EQUITY_ETFS);
}

export function isEmergingMarketsETF(symbol: string): boolean {
  return isInList(symbol, EMERGING_MARKETS_ETFS);
}

export function isUSBondETF(symbol: string): boolean {
  return isInList(symbol, US_BOND_ETFS);
}

export function isInternationalBondETF(symbol: string): boolean {
  return isInList(symbol, INTERNATIONAL_BOND_ETFS);
}

export function isRealEstateETF(symbol: string): boolean {
  return isInList(symbol, REAL_ESTATE_ETFS);
}

export function isCommoditiesETF(symbol: string): boolean {
  return isInList(symbol, COMMODITIES_ETFS);
}

export function isCryptoETF(symbol: string): boolean {
  return isInList(symbol, CRYPTO_ETFS);
}

/**
 * Get the granular asset class for an ETF symbol
 * @returns AssetClass if recognized, AssetClass.Other otherwise
 */
export function getAssetClass(symbol: string): AssetClass {
  // Check in order of specificity
  if (isUSEquityETF(symbol)) return AssetClass.UsEquity;
  if (isInternationalEquityETF(symbol)) return AssetClass.InternationalEquity;
  if (isEmergingMarketsETF(symbol)) return AssetClass.EmergingMarkets;
  if (isUSBondETF(symbol)) return AssetClass.UsBonds;
  if (isInternationalBondETF(symbol)) return AssetClass.InternationalBonds;
  if (isRealEstateETF(symbol)) return AssetClass.RealEstate;
  if (isCommoditiesETF(symbol)) return AssetClass.Commodities;
  if (isCryptoETF(symbol)) return AssetClass.Crypto;

  // Cash is handled separately (not ETFs)
  return AssetClass.Other;
}


